/**
 * ProductDetailPage V2
 *
 * Full Page Route für Produktdetails.
 * Route: /products/:productId
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 * Dark/Light Mode via Tailwind Standard
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { getProduct, getProductImages } from '@/lib/api/products';
import { lazySyncDescriptions } from '@/lib/api/descriptions';
import { fetchFieldMap } from '@/components/v2/tools/text/textGenerationApi';
import { AI_FIELD_MAP_KEY } from '@/hooks/useAIFieldMenuData';
import { ProductDetailLayout } from '@/components/products/v2/ProductDetailLayout';
import { Button, Alert, AlertDescription } from '@/components/v2/ui';
import { ReauthGate } from '@/components/v2/reauth/ReauthGate';

/**
 * Skeleton Loading für initiales Laden
 */
function PageSkeleton() {
  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
      {/* Header Skeleton */}
      <div className="sticky top-16 z-30 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-neutral-200 dark:bg-neutral-700 rounded-lg animate-pulse" />
              <div className="space-y-2">
                <div className="h-6 w-48 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
                <div className="h-4 w-32 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="h-8 w-24 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
              <div className="h-8 w-32 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Skeleton */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6 p-1 bg-neutral-100 dark:bg-neutral-800 rounded-lg w-fit">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div
              key={i}
              className="h-10 w-24 bg-neutral-200 dark:bg-neutral-700 rounded-md animate-pulse"
            />
          ))}
        </div>

        {/* Content Skeleton */}
        <div className="space-y-4">
          <div className="h-64 bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 p-6">
            <div className="space-y-3">
              <div className="h-5 w-32 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
              <div className="h-4 w-full bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
              <div className="h-4 w-3/4 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
              <div className="h-4 w-5/6 bg-neutral-200 dark:bg-neutral-700 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Error State Komponente
 */
function ErrorState({ message }: { message: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="mb-6">
          <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-xl w-fit mx-auto">
            <AlertTriangle className="h-12 w-12 text-red-600 dark:text-red-400" />
          </div>
        </div>
        <h3 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50 mb-2">
          {t('common.error')}
        </h3>
        <Alert variant="error" className="mb-6">
          <AlertDescription>{message}</AlertDescription>
        </Alert>
        <Button variant="primary" onClick={() => navigate('/v2/products')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('products.backToProductList')}
        </Button>
      </div>
    </div>
  );
}

/**
 * ProductDetailPage Hauptkomponente (innere Logik — über ReauthGate gewrappt)
 */
function ProductDetailPageV2Inner() {
  const { productId } = useParams<{ productId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Query für Produktdaten
  const productQuery = useQuery({
    queryKey: ['product', productId],
    queryFn: () => getProduct(productId!),
    enabled: !!productId,
    staleTime: 30_000, // 30 Sekunden
  });

  // Background-Prefetch: Bilder on-demand synchronisieren beim Seitenaufruf.
  // GET /products/{id}/images löst im Backend automatisch einen JTL-Sync aus,
  // falls keine Bilder in der DB vorhanden sind.
  // Bei Erfolg: Assets-Cache invalidieren damit der Tab die neuen Bilder zeigt.
  useQuery({
    queryKey: ['product-images', productId],
    queryFn: async () => {
      const result = await getProductImages(productId!);
      if (result.synced) {
        queryClient.invalidateQueries({ queryKey: ['product-assets', productId] });
      }
      return result;
    },
    enabled: !!productId,
    staleTime: 5 * 60_000, // 5 Minuten Cache
  });

  // Background: Descriptions on-demand synchronisieren beim Seitenaufruf.
  // POST /products/{id}/descriptions/lazy-sync loest diff-basierten JTL-Sync aus.
  // Nur bei Aenderungen werden neue Content Block Versions erstellt.
  useQuery({
    queryKey: ['descriptions-lazy-sync', productId],
    queryFn: async () => {
      const result = await lazySyncDescriptions(productId!)
      if (result.synced) {
        queryClient.invalidateQueries({ queryKey: ['channel-descriptions', productId] })
        queryClient.invalidateQueries({ queryKey: ['content-blocks', productId] })
      }
      return result
    },
    enabled: !!productId,
    staleTime: 5 * 60_000, // 5 Minuten Cache
  })

  // Prefetch: Text-Field-Map beim Seitenaufruf vorladen, damit das KI-Text-
  // Dropdown die (workspace-weit identische) Vorlagen-Liste sofort zeigt,
  // statt sie erst beim ersten Klick zu laden. Geteilter React-Query-Cache
  // mit useAIFieldMenuData (gleicher Key) → genau ein Request pro Workspace.
  useQuery({
    queryKey: AI_FIELD_MAP_KEY,
    queryFn: fetchFieldMap,
  });

  // Loading State - Skeleton statt Full Page Spinner
  if (productQuery.isLoading) {
    return <PageSkeleton />;
  }

  // Error State
  if (productQuery.isError) {
    return <ErrorState message={t('products.loadError')} />;
  }

  // Not Found State
  if (!productQuery.data?.product) {
    return <ErrorState message={t('products.notFound')} />;
  }

  // Success - Render Layout
  return (
    <ProductDetailLayout
      product={productQuery.data.product}
      onBack={() => navigate('/v2/products')}
    />
  );
}

/**
 * Exported wrapper: hard-blocks the page while JTL re-auth is pending,
 * otherwise renders the original product detail tree unchanged.
 */
export function ProductDetailPageV2() {
  return (
    <ReauthGate>
      <ProductDetailPageV2Inner />
    </ReauthGate>
  );
}
